import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Wrench, Settings, MapPin, User, Calendar, CreditCard as Edit, AlertTriangle, Camera, Eye, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ToolForm from '../components/tools/ToolForm';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';

const ToolDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showEditForm, setShowEditForm] = useState(false);
  const [tool, setTool] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  const isNewTool = id === 'new';

  useEffect(() => {
    if (!isNewTool && id) {
      loadTool();
    } else {
      setLoading(false);
    }
  }, [id, isNewTool]);


  const loadTool = async () => {
    try {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      // Parse images string to array
      if (data && data.images) {
        data.images = data.images.split(',').map((url: string) => url.trim()).filter((url: string) => url);
      } else if (data) {
        data.images = [];
      }

      setTool(data);
    } catch (error) {
      console.error('Error loading tool:', error);
      setTool(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isNewTool && !tool) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Tool Not Found</h2>
        <p className="text-gray-500 mb-6">The tool you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/tools')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tools
        </Button>
      </div>
    );
  }

  if (isNewTool || showEditForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/tools')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNewTool ? 'Add New Tool' : `Edit Tool: ${tool?.name}`}
          </h1>
        </div>
        
        <ToolForm 
          tool={tool} 
          isEdit={!isNewTool} 
        />
      </div>
    );
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'in_use': return 'primary';
      case 'maintenance': return 'warning';
      case 'retired': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Available';
      case 'in_use': return 'In Use';
      case 'maintenance': return 'Under Maintenance';
      case 'retired': return 'Retired';
      default: return status;
    }
  };

  const isMaintenanceDue = () => {
    if (!tool?.last_maintenance) return false;
    const lastMaintenance = new Date(tool.last_maintenance);
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    return lastMaintenance < threeMonthsAgo;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/tools')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/tools" className="text-sm text-gray-500 hover:text-gray-700">
                  Tools
                </Link>
              </li>
              <li>
                <span className="text-sm text-gray-600">/</span>
              </li>
              <li>
                <span className="text-sm text-gray-900">{tool?.name}</span>
              </li>
            </ol>
          </nav>
        </div>
        <Button onClick={() => setShowEditForm(true)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Tool
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="aspect-w-16 aspect-h-9 mb-6">
              {tool?.images && tool.images.length > 0 ? (
                <div className="space-y-2">
                  <img
                    src={tool.images[0]}
                    alt={tool.name}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                  {tool.images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {tool.images.slice(1).map((url: string, index: number) => (
                        <img
                          key={index}
                          src={url}
                          alt={`${tool.name} ${index + 2}`}
                          className="w-full h-20 object-cover rounded cursor-pointer hover:opacity-75 transition-opacity"
                          onClick={() => {
                            const newImages = [url, ...tool.images.filter((u: string) => u !== url)];
                            setTool({ ...tool, images: newImages });
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-64 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Wrench className="h-20 w-20 text-blue-600 mx-auto mb-4" />
                    <p className="text-lg font-medium text-blue-800">{tool?.category || 'Tool'}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">{tool?.name}</h1>
                <div className="flex gap-2">
                  <Badge variant={getStatusBadgeVariant(tool?.status || '')}>
                    {tool?.status}
                  </Badge>
                  {isMaintenanceDue() && (
                    <Badge variant="warning">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Maintenance Due
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {tool?.category && (
                  <Badge variant="secondary">{tool.category}</Badge>
                )}
                {tool?.images && tool.images.length > 0 && (
                  <Badge variant="primary">
                    <Camera className="h-3 w-3 mr-1" />
                    {tool.images.length} photos
                  </Badge>
                )}
                {tool?.serial_number && (
                  <span className="text-sm text-gray-500 font-mono">S/N: {tool.serial_number}</span>
                )}
              </div>

              <p className="text-gray-600">{tool?.description}</p>

              <div className="grid grid-cols-1 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm">Current Location</span>
                  </div>
                  <span className="text-lg font-medium text-gray-900">
                    {tool?.location}
                  </span>
                </div>

                {tool?.assigned_to && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-600 mb-1">
                      <User className="h-4 w-4" />
                      <span className="text-sm">Assigned To</span>
                    </div>
                    <span className="text-lg font-medium text-blue-900">
                      {tool.assigned_to}
                    </span>
                  </div>
                )}

                {tool?.project_code && (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 text-green-600 mb-1">
                      <Settings className="h-4 w-4" />
                      <span className="text-sm">Project</span>
                    </div>
                    <span className="text-lg font-medium text-green-900">
                      {tool.project_code}
                    </span>
                  </div>
                )}
              </div>

              {tool?.notes && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="text-sm font-medium text-amber-700 mb-2">Notes</h4>
                  <p className="text-sm text-amber-600">{tool.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Tool Information</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-gray-200">
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <dd className="text-sm text-gray-900 col-span-2">{tool?.category || 'N/A'}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Serial Number</dt>
                  <dd className="text-sm text-gray-900 col-span-2 font-mono">{tool?.serial_number || 'N/A'}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Purchase Date</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {tool?.purchase_date ? new Date(tool.purchase_date).toLocaleDateString() : 'N/A'}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Last Maintenance</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {tool?.last_maintenance ? new Date(tool.last_maintenance).toLocaleDateString() : 'N/A'}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    <Badge variant={getStatusBadgeVariant(tool?.status || '')}>
                      {getStatusLabel(tool?.status || '')}
                    </Badge>
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {isMaintenanceDue() && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 text-amber-600">
                  <AlertTriangle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Maintenance Due</p>
                    <p className="text-sm">
                      This tool hasn't been maintained in over 3 months. Please schedule maintenance.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {tool?.status === 'maintenance' && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 text-red-600">
                  <Settings className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Under Maintenance</p>
                    <p className="text-sm">
                      This tool is currently undergoing maintenance and is not available for use.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolDetailPage;